import { Match, Show, Switch, onMount, useContext } from 'solid-js';
import { Meta, Title } from '@solidjs/meta';
import { AppContext } from '../../app-context-provider/app-context-provider';
import theme from '../../style/theme';
import { PageCacheType } from '../../types/shared';
import { UserProfileDesktop } from './user-profile-desktop/user-profile-desktop';
import { UserProfileMobile } from './user-profile-mobile/user-profile-mobile';
import { logOut } from '../../tools/log-out';
import { AccountView } from '../../tools/account-slugs';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { MyContent } from '../my-content/my-content';
import { Section } from '../../grid-system/section/section';

export type UserProfileProps = {
    view: AccountView | 'default';
};

export const UserProfile = (props: UserProfileProps) => {
    const {
        viewport,
        siteInfo,
        userState,
        coursesListingPage,
        localize,
        setCacheType,
        appStore,
    } = useContext(AppContext);

    onMount(() => {
        setCacheType(PageCacheType.none);
    });

    // Resolve 'default' view to the actual view that should render based on site/feature flags.
    const resolvedView = (): AccountView => {
        if (props.view !== 'default') return props.view;
        if (siteInfo.siteType === 'atos-care') return 'orders';
        return coursesListingPage.url ? 'learning' : 'bookmarks';
    };

    const SignInPrompt = () => (
        <div class="flex flex-col items-start gap-cvi-6 max-w-[480px] mx-auto py-cvi-12 px-cvi-6">
            <Heading tag="h1" variant="heading-xl" noBlockSpacing>
                {localize('account-sign-in-cta-heading', 'Sign in to view your account')}
            </Heading>
            <Button
                label={localize('sign-in', 'Sign in')}
                variant="primary"
                onClick={() => appStore.openLoginMenu()}
            />
        </div>
    );

    // Bookmarks live entirely in the `myContent` cookie, so they are viewable without an account.
    // Render them on their own, with no sign-in friction: patients/caregivers should be able to
    // view what they saved without being prompted to log in.
    const AnonymousBookmarks = () => (
        <Section templateShorthand={[ 12 ]} widthType="bgFull" customCss={'padding-top: 0; padding-bottom: 0; min-height: 50dvh;'}>
            <main class="min-w-0 pt-cvi-9 pb-cvi-12">
                <MyContent />
            </main>
        </Section>
    );

    return (
        <>
            <Title>{`${localize('account-page-title', 'My Account')} - Atos Medical`}</Title>
            <Meta name="robots" content="noindex, nofollow" />

            <div class="[container-type:inline-size] overflow-clip">
                <Show
                    when={userState.user}
                    fallback={
                        // Only the explicit bookmarks route is public. Gate on props.view (not the
                        // resolvedView fallback) so the bare /my-account route never resolves an
                        // anonymous user into the bookmarks view by accident.
                        <Show when={props.view === 'bookmarks'} fallback={<SignInPrompt />}>
                            <AnonymousBookmarks />
                        </Show>
                    }
                >
                    <Switch>
                        <Match when={viewport.width > theme.breakpoints.TABLET}>
                            <UserProfileDesktop
                                user={userState.user}
                                view={resolvedView()}
                                handleSignOutClick={() => logOut(siteInfo.siteType)}
                            />
                        </Match>
                        <Match when={viewport.width <= theme.breakpoints.TABLET}>
                            <UserProfileMobile
                                user={userState.user}
                                view={resolvedView()}
                                handleSignOutClick={() => logOut(siteInfo.siteType)}
                            />
                        </Match>
                    </Switch>
                </Show>
            </div>
        </>
    );
};
